import {
    CreateMultiLegShippingEstimateByFuelResponse,
    EmissionFactorEstimate,
    MultiLegShippingEmissionEstimate,
    PassengerTransportationEmissionEstimate,
    SingleShippingEmissionEstimate,
    TransactionDocumentEmissionEstimate,
    TransactionEmissionEstimate,
} from '@lune-climate/lune'
import { SuccessResponseWithMeta } from '@lune-climate/lune/esm/core/SuccessResponse'
import {
    CalculationResults,
    EmissionFactorEstimateResult,
    flattenLegs,
    Leg,
    MultiLegShippingByFuelEstimateResult,
    PassengerTransportationEstimateResult,
    TransactionEstimateResult,
} from '@lune-fe/lune-components-lib'
import { LoadingWrapper, useIsMobile } from '@lune-fe/lune-ui-lib'
import Stack from '@mui/material/Stack'
import { FC, useEffect, useMemo, useState } from 'react'

import { EstimateType, EstimateTypeName } from 'views/CalculateEmissions'

import { MultiLegShippingEstimateDetails } from './components/MultiLegShippingEstimateDetails'
import { ShippingEstimateDetails } from './components/ShippingEstimateDetails'
import { TransactionDocumentEstimateDetails } from './components/TransactionDocumentEstimateDetails'

// Estimate types that have CalculationResults built into their result components
const ESTIMATE_TYPES_WITH_BUILTIN_HEADER: readonly EstimateType[] = [
    'transaction',
    'activity',
    'passenger_transportation',
    'multi_leg_shipping_by_fuel',
] as const

type EstimateUnion =
    | SuccessResponseWithMeta<SingleShippingEmissionEstimate>
    | SuccessResponseWithMeta<MultiLegShippingEmissionEstimate>
    | SuccessResponseWithMeta<CreateMultiLegShippingEstimateByFuelResponse>
    | SuccessResponseWithMeta<TransactionEmissionEstimate>
    | SuccessResponseWithMeta<EmissionFactorEstimate>
    | SuccessResponseWithMeta<PassengerTransportationEmissionEstimate>
    | SuccessResponseWithMeta<TransactionDocumentEmissionEstimate>
    | null

interface EmissionCalculationDetailsProps {
    estimate: EstimateUnion
    resolvedLegs: Leg[]
    loading: boolean
    estimateType: EstimateType
}
// eslint-disable-next-line complexity
const EmissionCalculationDetails: FC<EmissionCalculationDetailsProps> = ({
    estimate,
    resolvedLegs,
    loading,
    estimateType,
}) => {
    const { isMobile } = useIsMobile()
    const [localLoading, setLocalLoading] = useState<boolean>(true)

    useEffect(() => {
        /**
         * There are cases when an estimate loads before the side panel is fully loaded
         * and it causes the legs wrapper width to be calculated wrongly,
         * as the wrapper depends on parent position.
         *
         * To prevent this we need to make sure to show the estimate
         * only after panel is fully opened.
         *
         * Panel animation time is 0.3s
         */
        setTimeout(() => {
            setLocalLoading(loading)
        }, 300)
    }, [loading])

    const multiLegLegs: Leg[] = useMemo(() => {
        if (estimate && 'legs' in estimate && estimateType === 'multi_leg_shipping') {
            return flattenLegs(
                estimate as SuccessResponseWithMeta<MultiLegShippingEmissionEstimate>,
            )
        }
        return []
    }, [estimate, estimateType])

    const hasBuiltInCalculationResults = ESTIMATE_TYPES_WITH_BUILTIN_HEADER.includes(estimateType)

    return (
        <LoadingWrapper loading={localLoading} sx={{ height: 'calc(100vh - 100px)' }}>
            {estimate && !localLoading && (
                <>
                    {!hasBuiltInCalculationResults && (
                        <CalculationResults
                            title={
                                ('request' in estimate &&
                                    typeof estimate.request === 'object' &&
                                    'name' in estimate.request &&
                                    estimate.request.name) ||
                                EstimateTypeName(estimateType)
                            }
                            amount={estimate.mass.amount}
                            dataQualityScore={
                                'dataQualityScore' in estimate
                                    ? estimate.dataQualityScore?.score
                                    : undefined
                            }
                        />
                    )}

                    <Stack
                        direction={'column'}
                        spacing={9}
                        mt={hasBuiltInCalculationResults ? 0 : 9}
                    >
                        {/* Shipping estimate - Single or Multi-leg */}
                        {estimateType === 'shipping' && 'request' in estimate && (
                            <ShippingEstimateDetails
                                estimate={
                                    estimate as SuccessResponseWithMeta<SingleShippingEmissionEstimate>
                                }
                                resolvedLegs={resolvedLegs}
                            />
                        )}

                        {/* Multi-leg shipping estimate */}
                        {estimateType === 'multi_leg_shipping' && 'request' in estimate && (
                            <MultiLegShippingEstimateDetails
                                estimate={
                                    estimate as SuccessResponseWithMeta<MultiLegShippingEmissionEstimate>
                                }
                                legs={multiLegLegs}
                            />
                        )}

                        {/* Multi-leg shipping by fuel estimate */}
                        {estimateType === 'multi_leg_shipping_by_fuel' && (
                            <MultiLegShippingByFuelEstimateResult
                                estimate={
                                    estimate as SuccessResponseWithMeta<CreateMultiLegShippingEstimateByFuelResponse>
                                }
                                enableEmissionFactorLink={true}
                                isMobile={isMobile}
                            />
                        )}

                        {/* Transaction estimate */}
                        {estimateType === 'transaction' && (
                            <TransactionEstimateResult
                                estimate={
                                    estimate as SuccessResponseWithMeta<TransactionEmissionEstimate>
                                }
                                enableEmissionFactorLink={true}
                            />
                        )}

                        {/* Emission factor estimate */}
                        {estimateType === 'activity' && (
                            <EmissionFactorEstimateResult
                                estimate={
                                    estimate as SuccessResponseWithMeta<EmissionFactorEstimate>
                                }
                                enableEmissionFactorLink={true}
                            />
                        )}

                        {/* Passenger transportation estimate */}
                        {estimateType === 'passenger_transportation' && (
                            <PassengerTransportationEstimateResult
                                estimate={
                                    estimate as SuccessResponseWithMeta<PassengerTransportationEmissionEstimate>
                                }
                                enableEmissionFactorLink={true}
                                isMobile={isMobile}
                            />
                        )}

                        {/* Transaction document estimate */}
                        {estimateType === 'transaction_document' && (
                            <TransactionDocumentEstimateDetails
                                estimate={
                                    estimate as SuccessResponseWithMeta<TransactionDocumentEmissionEstimate>
                                }
                            />
                        )}
                    </Stack>
                </>
            )}
        </LoadingWrapper>
    )
}

export default EmissionCalculationDetails
